import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, 
  CheckCircle, 
  FileText, 
  Calculator, 
  DollarSign, 
  Shield, 
  Star,
  Users,
  TrendingUp,
  Download,
  Clock,
  Zap,
  CreditCard,
  Lock,
  X
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pay-per-use");

  const features = [
    {
      icon: <FileText className="w-8 h-8 text-blue-600" />,
      title: "PDF Bank Statement Processing",
      description: "Upload any PDF bank statement and watch our AI instantly extract and categorize all your transactions."
    },
    {
      icon: <Calculator className="w-8 h-8 text-blue-600" />,
      title: "Automatic Expense Categorization",
      description: "Smart AI categorizes your business expenses into proper tax categories automatically, saving hours of manual work."
    },
    {
      icon: <DollarSign className="w-8 h-8 text-blue-600" />,
      title: "Schedule C Tax Form Ready",
      description: "Get tax-ready reports mapped directly to IRS Schedule C line items for seamless tax filing."
    },
    {
      icon: <Download className="w-8 h-8 text-blue-600" />,
      title: "Professional Excel Reports",
      description: "Export comprehensive tax analysis reports in Excel format for your accountant or personal records."
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: "Secure Cloud",
      description: "Our website is encrypted and secured with bank-level security."
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Instant Results",
      description: "Process months of bank statements in seconds, not hours. Get your tax analysis instantly."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Small Business Owner",
      content: "I love the pay-per-use model! Only paid $15 for my quarterly statements instead of a monthly subscription.",
      rating: 5
    },
    {
      name: "Mike Chen", 
      role: "Freelance Consultant",
      content: "Perfect for occasional use. Processed 3 statements this year for just $8 total. Much better than subscriptions!",
      rating: 5
    },
    {
      name: "Lisa Rodriguez",
      role: "Restaurant Owner",
      content: "The credit packages are great value. Got 25% off with the business package for my monthly processing.",
      rating: 5
    }
  ];

  const pricingPlans = {
    payPerUse: {
      name: "Pay As You Go",
      basePrice: "$1.00",
      pagePrice: "$1.00",
      description: "Perfect for occasional users - no commitments",
      features: [
        "$1 per PDF upload",
        "$1 per page processed", 
        "No monthly fees",
        "Full tax categorization",
        "Schedule C integration",
        "Excel export included"
      ]
    },
    creditPackages: [
      {
        id: "starter",
        name: "Starter Package",
        credits: 7,
        price: "$5.00",
        originalPrice: "$7.00",
        savings: "Save 30%",
        description: "Perfect for small businesses",
        features: [
          "7 processing credits",
          "30% discount",
          "Credits never expire",
          "All premium features",
          "Email support"
        ]
      },
      {
        id: "professional", 
        name: "Professional Package",
        credits: 20,
        price: "$12.00",
        originalPrice: "$20.00", 
        savings: "Save 40%",
        description: "Great for regular users",
        popular: true,
        features: [
          "20 processing credits",
          "40% discount", 
          "Credits never expire",
          "All premium features",
          "Priority support"
        ]
      },
      {
        id: "business",
        name: "Business Package", 
        credits: 45,
        price: "$22.50",
        originalPrice: "$45.00",
        savings: "Save 50%", 
        description: "Best value for businesses",
        features: [
          "45 processing credits",
          "50% discount",
          "Credits never expire", 
          "All premium features",
          "Dedicated support"
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/find-tax-pro')}
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                data-testid="search-tax-pros-btn"
              >
                Find a Tax Pro
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth')}
                className="text-slate-600 hover:text-slate-800"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="get-started-btn"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          {/* Tax Helper Pros with Logo */}
          <div className="flex items-center justify-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <Calculator className="w-7 h-7 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Tax Helper Pros
            </h2>
          </div>

          {/* Two-option hero headline */}
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 leading-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <span className="text-blue-600">HIRE A TAX HELPER PRO</span>
              <br />
              <span>to do your Taxes!</span>
            </h1>
            <p className="text-2xl sm:text-3xl font-bold text-slate-400 my-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Or
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 leading-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              <span>TRY our</span>
              <br />
              <span className="text-red-600 animate-pulse" style={{textShadow: '0 0 20px rgba(220, 38, 38, 0.3)'}}>AI Bank Statement Reader</span>
            </h1>
          </div>

          {/* Two CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10">
            <Button
              size="lg"
              onClick={() => navigate('/find-tax-pro')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg"
              data-testid="hero-hire-pro-btn"
            >
              <Users className="w-5 h-5 mr-2" />
              Find a Tax Pro
            </Button>
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
              data-testid="hero-cta-btn"
            >
              Try the AI Reader
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <Badge variant="outline" className="mb-6 bg-blue-50 border-blue-200 text-blue-700">
            Pay Only For What You Use - No Subscriptions!
          </Badge>
          
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Pay-Per-Use Bank Statement Organizer
            <br />
            <span className="text-blue-600">$1 Upload + $1 Per Page</span>
          </h3>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed" style={{fontFamily: 'Inter, sans-serif'}}>
            No monthly subscriptions, no commitments. Upload your bank statements, pay only for processing, 
            and get professional tax reports instantly. Perfect for businesses of all sizes.
          </p>
          
          {/* Security Message */}
          <div className="flex items-center justify-center gap-3 mb-12 bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
            <Lock className="w-6 h-6 text-green-600 flex-shrink-0" />
            <p className="text-green-800 font-medium" style={{fontFamily: 'Inter, sans-serif'}}>
              All data is encrypted, secure, and deleted. We DO NOT store your data.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
            >
              Get Started Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => document.getElementById('pricing').scrollIntoView({behavior: 'smooth'})}
              className="px-8 py-4 text-lg border-2"
            >
              See Pricing
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">$1</div>
              <div className="text-slate-600">Per PDF Upload</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">$1</div>
              <div className="text-slate-600">Per Page Processed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">$0</div>
              <div className="text-slate-600">Monthly Fees</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-white/50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Professional Tax Tools, Pay-Per-Use Pricing
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Get enterprise-grade tax processing without the enterprise price tag
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-slate-600 text-center">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Pay only for what you process. No hidden fees, no monthly commitments.
            </p>
            
            {/* Pricing Toggle */}
            <div className="flex justify-center mb-12">
              <div className="bg-white p-1 rounded-xl shadow-lg">
                <button
                  onClick={() => setActiveTab("pay-per-use")}
                  className={`px-6 py-3 rounded-lg transition-all ${
                    activeTab === "pay-per-use" 
                      ? "bg-blue-600 text-white shadow-md" 
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                  data-testid="pay-per-use-tab"
                >
                  <CreditCard className="w-4 h-4 mr-2 inline" />
                  Pay Per Use
                </button>
                <button
                  onClick={() => setActiveTab("credit-packages")}
                  className={`px-6 py-3 rounded-lg transition-all ${
                    activeTab === "credit-packages" 
                      ? "bg-blue-600 text-white shadow-md" 
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                  data-testid="credit-packages-tab"
                >
                  <Zap className="w-4 h-4 mr-2 inline" />
                  Credit Packages
                  <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 text-xs">
                    SAVE UP TO 25%
                  </Badge>
                </button>
              </div>
            </div>
          </div>
          
          {/* Pay Per Use */}
          {activeTab === "pay-per-use" && (
            <div className="max-w-md mx-auto">
              <Card className="border-2 border-blue-200 shadow-2xl">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <CreditCard className="w-8 h-8 text-blue-600" />
                  </div>
                  <CardTitle className="text-2xl mb-2">{pricingPlans.payPerUse.name}</CardTitle>
                  <div className="mb-4">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-slate-800">{pricingPlans.payPerUse.basePrice}</span>
                      <span className="text-slate-600 ml-2">upload</span>
                      <span className="text-slate-500 mx-2">+</span>
                      <span className="text-3xl font-bold text-slate-800">{pricingPlans.payPerUse.pagePrice}</span>
                      <span className="text-slate-600 ml-2">per page</span>
                    </div>
                  </div>
                  <CardDescription className="text-center">
                    {pricingPlans.payPerUse.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="px-6 pb-8">
                  <ul className="space-y-4 mb-8">
                    {pricingPlans.payPerUse.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    size="lg" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
                    onClick={() => navigate('/auth')}
                    data-testid="pay-per-use-cta-btn"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  
                  <div className="text-center mt-4">
                    <p className="text-xs text-slate-500">Example: 5-page statement = $6.00 total</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Credit Packages */}
          {activeTab === "credit-packages" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {pricingPlans.creditPackages.map((pkg, index) => (
                <Card key={pkg.id} className={`relative border-2 shadow-xl transition-all duration-300 ${
                  pkg.popular 
                    ? "border-blue-500 scale-105" 
                    : "border-slate-200 hover:border-blue-300"
                }`}>
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white px-4 py-1">
                        Most Popular
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                      pkg.popular ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-green-100'
                    }`}>
                      <Zap className={`w-8 h-8 ${pkg.popular ? 'text-white' : 'text-green-600'}`} />
                    </div>
                    <CardTitle className="text-2xl mb-2">{pkg.name}</CardTitle>
                    <div className="mb-4">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-4xl font-bold text-slate-800">{pkg.price}</span>
                        <div className="text-left">
                          <div className="text-sm text-slate-500 line-through">{pkg.originalPrice}</div>
                          <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700 text-xs">
                            {pkg.savings}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm mt-2">{pkg.credits} processing credits</p>
                    </div>
                    <CardDescription className="text-center">
                      {pkg.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="px-6 pb-8">
                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      size="lg" 
                      className={`w-full py-3 ${
                        pkg.popular 
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                          : 'bg-blue-600 hover:bg-blue-700'
                      } text-white`}
                      onClick={() => navigate('/auth')}
                      data-testid={`${pkg.id}-package-cta-btn`}
                    >
                      Buy {pkg.name}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Why Choose Pay-Per-Use?
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Traditional Subscriptions */}
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-red-700 text-center">Traditional Subscriptions - COMPETITORS</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center text-red-700">
                    <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    Pay $30+/month even if unused
                  </li>
                  <li className="flex items-center text-red-700">
                    <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    Forced to commit to annual plans
                  </li>
                  <li className="flex items-center text-red-700">
                    <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    Wasted money during off-seasons
                  </li>
                  <li className="flex items-center text-red-700">
                    <X className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    Complex cancellation processes
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Our Pay-Per-Use */}
            <Card className="border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-700 text-center">Tax Helper Pros</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Pay only when you process files
                  </li>
                  <li className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    No monthly commitments ever
                  </li>
                  <li className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Credits never expire
                  </li>
                  <li className="flex items-center text-green-700">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    Use anytime, anywhere
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-slate-100">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Customers Love Pay-Per-Use
            </h2>
            <p className="text-xl text-slate-600">
              See what business owners are saying about our flexible pricing
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <div className="font-semibold text-slate-800">{testimonial.name}</div>
                    <div className="text-slate-600 text-sm">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Ready to Pay Only For What You Use?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            No subscriptions, no commitments, just professional tax processing when you need it. Buy credits and use them anytime.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/auth')}
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold"
              data-testid="final-cta-btn"
            >
              Get Started Today
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          <div className="flex justify-center items-center mt-8 space-x-8 text-blue-100">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              <span>Bank-Level Security</span>
            </div>
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              <span>Instant Processing</span>
            </div>
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              <span>No Monthly Fees</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Tax Helper Pros
                </span>
              </div>
              <p className="text-slate-400 mb-4">
                Professional tax preparation tools with pay-per-use pricing.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Pricing</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <button 
                    onClick={() => navigate('/pay-per-use')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Pay Per Use
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/credit-packages')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Credit Packages
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/bulk-discounts')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Bulk Discounts
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/free-credits')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Free Credits
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <button 
                    onClick={() => navigate('/help')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Help Center
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/contact')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Contact Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/tax-guide')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Tax Guide
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/api-docs')}
                    className="hover:text-white transition-colors text-left"
                  >
                    API Docs
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <button 
                    onClick={() => navigate('/about')}
                    className="hover:text-white transition-colors text-left"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/privacy')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/terms')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => navigate('/security')}
                    className="hover:text-white transition-colors text-left"
                  >
                    Security
                  </button>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400">
            <p>&copy; 2024 Tax Helper Pros. All rights reserved. Pay-per-use pricing - No subscriptions, ever!</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;